// Snapshot of Moyee's own published blog stories, taken from the public Odoo
// blog (https://www.moyeecoffee.com/blog) on 2026-08-24.
// WHY this exists (quest D10, founder decision 2026-08-24): the live news
// fetch cannot succeed from a browser today - the old WordPress API is gone
// and no endpoint on moyeecoffee.com sends a CORS header (evidence in
// AUDIT-FINDINGS.md, addendum 2026-08-24). Until the Odoo feed is
// CORS-enabled (quest M1), these dated stories are shown when the live fetch
// fails, always labelled with SNAPSHOT_AS_AT so a visitor never mistakes
// them for live data (CLAUDE.md §10). Refreshing them means re-running the
// capture and redeploying; the "as at" label makes that staleness visible.
//
// DESIGN (founder decision 2026-08-24, late batch, supersedes per-section
// category filtering): every Latest News section shows the SAME list - the
// latest 10 posts across ALL blog categories, newest first, including
// "our-stories". The category field is kept as provenance only. Blog
// tagging (quest M2) is optional polish now, not a blocker.
//
// Capture method: story text from the per-category Atom feeds
// (/blog/<category>/feed - five categories carry posts; freshdrip-journal
// and pricing-crisis are empty); cover images from the category listing
// pages' card markup (background-image URL, paired to each post by its
// data-res-id, which equals the trailing number of the post URL). Do NOT
// capture images from og:image meta tags: most pointed at dead internal
// Odoo domains on 2026-08-24 (recorded in AUDIT-FINDINGS.md). All 10 image
// URLs below were verified HTTP 200 on www.moyeecoffee.com at capture time.
// Images HOTLINK from moyeecoffee.com - if that site removes an image, the
// card falls back to the branded block (see News.tsx).

export const SNAPSHOT_AS_AT = '2026-08-24';
/** Same date, as shown to visitors in the "as at" label. */
export const SNAPSHOT_AS_AT_DISPLAY = '24 August 2026';

export type SnapshotCategory = 'farmers' | 'forests' | 'factories' | 'worldwide' | 'our-stories';

export interface SnapshotStory {
  category: SnapshotCategory;
  title: string;
  url: string;
  /** Date as published in Moyee's own feed (its <updated> field), YYYY-MM-DD. */
  date: string;
  summary: string;
  /** Cover image, hotlinked from moyeecoffee.com; verified 200 at capture. */
  image?: string;
}

// Superseded 2026-08-24 (founder design change: one shared latest-10 list for
// every section; kept per CLAUDE.md §2, not deleted). The Dutch blog_filter
// values from Fairchain's layers no longer select content.
export const BLOG_FILTER_TO_CATEGORY: Record<string, SnapshotCategory> = {
  boeren: 'farmers',
  bossen: 'forests',
  bedrijven: 'factories',
  europe: 'worldwide',
};

// Superseded 2026-08-24 - same reason as above. Use SNAPSHOT_STORIES directly.
export const storiesForBlogFilter = (blogFilter: string | undefined): SnapshotStory[] => {
  const category = BLOG_FILTER_TO_CATEGORY[(blogFilter ?? '').toLowerCase()];
  return category ? SNAPSHOT_STORIES.filter((s) => s.category === category) : [];
};

/** The latest 10 posts across all blog categories, newest first. */
export const SNAPSHOT_STORIES: SnapshotStory[] = [
  {
    category: 'our-stories',
    title: 'Founder’s Notes from Ethiopia',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/founders-notes-from-ethiopia-96',
    date: '2026-08-24',
    summary:
      'Hey Moyeesta, Our tour guide Guido was back in Ethiopia. To visit coffee farmers, catch up with our teams, and see how things are going with our coffee, our forests, and our plans. But first, he still...',
    image:
      'https://www.moyeecoffee.com/web/image/168876-76872b9e/Blog%20Post%20%27Founders%20notes%20vanuit%20Ethiopi%C3%AB%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'BREWING COFFEE: ALL METHODS COMPARED',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/brewing-coffee-all-methods-compared-94',
    date: '2026-08-17',
    summary:
      'Coffee brewing: all the methods at a glance. There are about twelve ways to turn a coffee bean into a great cup of coffee. And for every method, there’s someone online ready to tell you that their way...',
    image:
      'https://www.moyeecoffee.com/web/image/155786-caf962af/Blog%20Post%20%27KOFFIE%20ZETTEN%3A%20ALLE%20METHODES%20VERGELEKEN%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'Discover the story behind our African microlots',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/discover-the-story-behind-our-african-microlots-92',
    date: '2026-07-23',
    summary:
      "​ Ever wondered where our insanely delicious Microlots come from? This month we're heading to Nairobi, where Emily sat down with our coffee wizard Tess, our head of Coffee Quality, to talk tiny harves...",
    image:
      'https://www.moyeecoffee.com/web/image/120755-2e5a580a/Blog%20Post%20%27Discover%20the%20story%20behind%20our%20African%20microlots%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'REPUBLIC CAFE IN THE WORKS',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/republic-cafe-in-the-works-81',
    date: '2025-12-22',
    summary:
      'In just a few months, we’re opening the doors to something BIG: the first-ever Republic Café, right in the heart of Westlands Nairobi. We’ll be serving our coffee, roasted locally and brewed by people...',
    image:
      'https://www.moyeecoffee.com/web/image/9945-46773dd4/Blog%20Post%20%27REPUBLIC%20CAFE%20IN%20THE%20WORKS%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'FELIX, OUR ROASTER AT FAIRCHAIN KENYA, WINS BARISTA CHAMPIONSHIPS!',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/felix-our-roaster-at-fairchain-kenya-wins-barista-championships-82',
    date: '2025-12-21',
    summary:
      'Felix Ouma, our talented roaster at FairChain Coffee Kenya won the Kenya Barista Championships! For those in the know, Felix is the second Moyee FairChain barista to bring home this prestigious prize....',
    image:
      'https://www.moyeecoffee.com/web/image/9947-c48c5dd6/Blog%20Post%20%27FELIX%2C%20OUR%20ROASTER%20AT%20FAIRCHAIN%20KENYA%2C%20WINS%20BARISTA%20CHAMPIONSHIPS%21%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'THE REPUBLIC ON THE RISE IN EAST-AFRICA',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/the-republic-on-the-rise-in-east-africa-80',
    date: '2025-12-19',
    summary:
      'What started as sharefunding missions in 2023 and 2024 have evolved into something bigger, cooler and much more important than coffee alone. The Republic of Everyone is fuelling a revolution! You migh...',
    image:
      'https://www.moyeecoffee.com/web/image/9944-8c94083e/Blog%20Post%20%27THE%20REPUBLIC%20ON%20THE%20RISE%20IN%20EAST-AFRICA%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'LEGER DES HEILS X MOYEE COFFEE',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/leger-des-heils-x-moyee-coffee-78',
    date: '2025-11-28',
    summary:
      'The Salvation Army and Moyee Coffee are joining forces! Step by step, the Salvation Army is switching to 100% FairChain coffee with a co-branded collaboration that puts this important organization in ...',
    image:
      'https://www.moyeecoffee.com/web/image/14207-a8c0a783/Blog%20Post%20%27LEGER%20DES%20HEILS%20X%20MOYEE%20COFFEE%27%20cover%20image.webp',
  },
  {
    category: 'our-stories',
    title: 'FEMALE-POWERED CHILD LABOUR PROJECT IN KENYA',
    url: 'https://www.moyeecoffee.com/blog/our-stories-2/female-powered-child-labour-project-in-kenya-70',
    date: '2025-11-04',
    summary:
      'In Kericho, Kenya, a grassroots anti-Child Labor revolution is unfolding, powered by 150 women who diversified their coffee cherry picking with gooseberries and chickens on one-hectare community plots...',
    image:
      'https://www.moyeecoffee.com/web/image/9931-8ee000cd/Blog%20Post%20%272025%20CHILD%20LABOUR%20PROJECT%27%20cover%20image.webp',
  },
  {
    category: 'farmers',
    title: 'MOYEE ON TV: REALLY HONEST FOR THE COFFEE FARMER',
    url: 'https://www.moyeecoffee.com/blog/farmers-4/moyee-on-tv-really-honest-for-the-coffee-farmer-7',
    date: '2025-04-20',
    summary:
      'One of Holland’s best-known consumer report programs gave FairChain some airtime. More to the point, they compared FairChain to Fairtrade and discussed the high potential of FairChain with Dutch profe...',
    image: 'https://www.moyeecoffee.com/web/image/4234-c4c5e9cd/Processing%202.webp',
  },
  {
    category: 'farmers',
    title: 'DIGITALIZATION: USING DATA TO PAY FARMERS AND PROTECT FORESTS!',
    url: 'https://www.moyeecoffee.com/blog/farmers-4/digitalization-using-data-to-pay-farmers-and-protect-forests-67',
    date: '2025-03-12',
    summary:
      'The use of data is key in Western businesses, but it’s just as crucial in the coffee forests of East Africa! Since 2017, Moyee has been actively collecting and analyzing data to gain deeper insights i...',
    image:
      'https://www.moyeecoffee.com/web/image/5444-7f1f2660/Blog%20Post%20%27DIGITALIZATION%3A%20USING%20DATA%20TO%20PAY%20FARMERS%20AND%20PROTECT%20FORESTS%21%27%20cover%20image.webp',
  },
];
